import React, { useEffect, useMemo, useState } from "react";

import {

  Button,

  DatePicker,

  Input,

  InputNumber,

  Modal,

  Pagination,

  Select,

  Spin,

  Table,

  Tag,

  Typography,

  message,

} from "antd";

import {

  EyeOutlined,

  HomeOutlined,

  ReloadOutlined,

  RightOutlined,

  SearchOutlined,

} from "@ant-design/icons";

import dayjs from "dayjs";

import "./exam.css";

const { Title, Text } = Typography;

const { RangePicker } = DatePicker;

const EXAM_API_BASE =

  process.env.REACT_APP_EXAM_API_BASE_URL ||

  "http\://localhost:5000/api/exams";

const DEFAULT_PAGE_SIZE = 10;

const parseJsonSafely = async (response) => {

  const raw = await response.text();

  if (!raw) {

    return {

      status: response.ok,

      message: response.ok ? "Request completed" : "Request failed",

    };

  }

  try {

    return JSON.parse(raw);

  } catch {

    return {

      status: false,

      message: `Server returned an invalid response (${response.status})`,

    };

  }

};

const formatDateTime = (value) => {

  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString();

};

const formatLevel = (value) => {

  if (!value) return "-";

  return String(value)

    .replaceAll("_", " ")

    .replace(/\b\w/g, (char) => char.toUpperCase());

};

const getDepartmentName = (record) =>

  record?.department_name ||

  record?.department ||

  record?.dept_name ||

  "";

const getStatusTag = (status) => {

  const normalized = String(status || "").toLowerCase();

  return (

    <Tag color={normalized === "submitted" ? "green" : "default"}>

      {normalized

        ? normalized.charAt(0).toUpperCase() + normalized.slice(1)

        : "-"}

    </Tag>

  );

};

export default function AssessmentResultsPage() {

  const [loading, setLoading] = useState(true);

  const departmentLoading = false;

  const [rows, setRows] = useState([]);

  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [searchInput, setSearchInput] = useState("");

  const [search, setSearch] = useState("");

  const [departmentId, setDepartmentId] = useState("");

  const [examId, setExamId] = useState("");

  const [level, setLevel] = useState("");

  const [unitCode, setUnitCode] = useState("");

  const [attemptNo, setAttemptNo] = useState("");

  const [status, setStatus] = useState("submitted");

  const [dateRange, setDateRange] = useState(null);

  const [departmentOptions, setDepartmentOptions] = useState([]);

  const [examOptions, setExamOptions] = useState([]);

  const [unitOptions, setUnitOptions] = useState([]);

  const [viewModalOpen, setViewModalOpen] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);

  const loadResults = async () => {

    try {

      setLoading(true);

      const params = new URLSearchParams({

        page: String(page),

        limit: String(pageSize),

      });

      if (departmentId) params.set("department_id", String(departmentId));

      if (search) params.set("search", search);

      if (examId) params.set("exam_id", String(examId));

      if (level) params.set("level", level);

      if (unitCode) params.set("unit_code", String(unitCode));

      if (attemptNo) params.set("attempt_no", String(attemptNo));

      if (status) params.set("status", status);

      if (

        Array.isArray(dateRange) &&

        dateRange.length === 2 &&

        dateRange[0] &&

        dateRange[1]

      ) {

        params.set("from_date", dayjs(dateRange[0]).format("YYYY-MM-DD"));

        params.set("to_date", dayjs(dateRange[1]).format("YYYY-MM-DD"));

      }

      const response = await fetch(

        `${EXAM_API_BASE}/assessment-results?${params.toString()}`

      );

      const result = await parseJsonSafely(response);

      if (!response.ok || !result?.status) {

        message.error(result?.message || "Unable to load assessment results");

        setRows([]);

        setTotal(0);

        return;

      }

      const list = Array.isArray(result?.data)

        ? result.data

        : Array.isArray(result?.data?.items)

        ? result.data.items

        : [];

      const pagination =

        result?.pagination ||

        result?.data?.pagination ||

        {};

      const filters =

        result?.filters ||

        result?.data?.filters ||

        {};

      setRows(list);

      setTotal(

        Number(

          pagination.total ??

            result?.total ??

            list.length

        ) || 0

      );

      if (Array.isArray(filters.departments)) {

        setDepartmentOptions(

          filters.departments.map((item) => {

            const deptId = item.department_id;

            const deptName =

              item.department_name ||

              item.department ||

              item.dept_name ||

              `Department ${deptId}`;

            return {

              value: String(deptId),

              selectedLabel: deptName,

              searchText: `${deptName} ${deptId}`,

              department_name: deptName,

              department_id: deptId,

              label: (

                <div className="manage-training-dept-option">

                  <div className="manage-training-dept-option-copy">

                    <strong>{deptName}</strong>

                  </div>

                </div>

              ),

            };

          })

        );

      } else {

        setDepartmentOptions([]);

      }

      if (Array.isArray(filters.exams)) {

        setExamOptions(

          filters.exams.map((item) => ({

            value: String(item.exam_id),

            label: `${item.exam_name || `Exam ${item.exam_id}`} - ${formatLevel(

              item.level

            )}`,

          }))

        );

      } else {

        setExamOptions([]);

      }

      if (Array.isArray(filters.units)) {

        setUnitOptions(

          filters.units.map((item) => ({

            value: String(item.unit_code),

            label: item.unit_name

              ? `${item.unit_code} - ${item.unit_name}`

              : String(item.unit_code),

          }))

        );

      } else {

        setUnitOptions([]);

      }

    } catch (error) {

      console.error("Assessment results load error:", error);

      message.error("Unable to connect to the exam server");

      setRows([]);

      setTotal(0);

    } finally {

      setLoading(false);

    }

  };

  useEffect(() => {

    loadResults();

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [

    page,

    pageSize,

    search,

    departmentId,

    examId,

    level,

    unitCode,

    attemptNo,

    status,

    dateRange,

  ]);

  const handleSearch = () => {

    setPage(1);

    setSearch(searchInput.trim());

  };

  const handleReset = () => {

    setSearchInput("");

    setSearch("");

    setDepartmentId("");

    setExamId("");

    setLevel("");

    setUnitCode("");

    setAttemptNo("");

    setStatus("submitted");

    setDateRange(null);

    setPage(1);

    setPageSize(DEFAULT_PAGE_SIZE);

  };

  const departmentNameById = useMemo(() => {

    const map = new Map();

    departmentOptions.forEach((item) => {

      map.set(String(item.value), item.department_name);

    });

    return map;

  }, [departmentOptions]);

  const columns = [

    {

      title: "Sl. No.",

      width: 78,

      fixed: "left",

      render: (_, __, index) => (page - 1) * pageSize + index + 1,

    },

    {

      title: "User",

      width: 210,

      render: (_, record) => (

        <div className="assessment-user-cell">

          <strong>{record.full_name || record.username || "-"}</strong>

          <span>Username: {record.username || "-"}</span>

          <span>User ID: {record.user_id ?? "-"}</span>

        </div>

      ),

    },

    {

      title: "Department",

      width: 190,

      render: (_, record) => (

        <div className="assessment-unit-cell">

          <strong>

            {getDepartmentName(record) ||

              departmentNameById.get(String(record.department_id)) ||

              `Department ${record.department_id || "-"}`}

          </strong>

        </div>

      ),

    },

    {

      title: "Unit",

      width: 155,

      render: (_, record) => (

        <div className="assessment-unit-cell">

          <strong>{record.unit_name || "-"}</strong>

          <span>{record.unit_code ?? "-"}</span>

        </div>

      ),

    },

    {

      title: "Total Attempts",

      dataIndex: "total_attempts",

      width: 120,

      align: "center",

      render: (value) => <strong>{Number(value || 0)}</strong>,

    },

    {

      title: "Latest Exam",

      width: 250,

      render: (_, record) => {

        const latest = record.latest_attempt || {};

        return (

          <div className="manage-exam-name">

            <strong>{latest.exam_name || "-"}</strong>

            <span>Exam ID: {latest.exam_id ?? "-"}</span>

          </div>

        );

      },

    },

    {

      title: "Latest Level",

      width: 115,

      align: "center",

      render: (_, record) => (

        <Tag color="blue">

          {formatLevel(record.latest_attempt?.level)}

        </Tag>

      ),

    },

    {

      title: "Latest Attempt",

      width: 115,

      align: "center",

      render: (_, record) => record.latest_attempt?.attempt_no ?? "-",

    },

    {

      title: "Latest Marks",

      width: 110,

      align: "center",

      render: (_, record) => {

        const latest = record.latest_attempt || {};

        return (

          <strong>

            {Number(latest.obtained_marks || 0)}/

            {Number(latest.total_marks || 0)}

          </strong>

        );

      },

    },

    {

      title: "Latest Score",

      width: 105,

      align: "center",

      render: (_, record) => (

        <strong className="assessment-score">

          {Number(record.latest_attempt?.percentage || 0)}%

        </strong>

      ),

    },

    {

      title: "Latest Status",

      width: 115,

      render: (_, record) => getStatusTag(record.latest_attempt?.status),

    },

    {

      title: "Latest Activity",

      dataIndex: "latest_activity_at",

      width: 180,

      render: formatDateTime,

    },

    {

      title: "Action",

      width: 105,

      fixed: "right",

      render: (_, record) => (

        <Button

          size="small"

          icon={<EyeOutlined />}

          className="assessment-results-view-button"

          disabled={!Array.isArray(record.attempts) || record.attempts.length === 0}

          onClick={() => {

            setSelectedUser(record);

            setViewModalOpen(true);

          }}

        >

          View

        </Button>

      ),

    },

  ];

  const attemptColumns = [
    {
      title: "Level",
      dataIndex: "level",
      width: 82,
      align: "center",
      render: (value) => <Tag color="blue">{formatLevel(value)}</Tag>,
    },
    {
      title: "Questions",
      dataIndex: "total_questions",
      width: 82,
      align: "center",
    },
    {
      title: "Correct",
      dataIndex: "correct_count",
      width: 72,
      align: "center",
      render: (value) => (
        <strong className="assessment-correct">{Number(value || 0)}</strong>
      ),
    },
    {
      title: "Wrong",
      dataIndex: "wrong_count",
      width: 68,
      align: "center",
      render: (value) => (
        <strong className="assessment-wrong">{Number(value || 0)}</strong>
      ),
    },
    {
      title: "Unanswered",
      dataIndex: "unanswered_count",
      width: 92,
      align: "center",
      render: (value) => Number(value || 0),
    },
    {
      title: "Marks",
      width: 76,
      align: "center",
      render: (_, record) => (
        <strong>
          {Number(record.obtained_marks || 0)}/{Number(record.total_marks || 0)}
        </strong>
      ),
    },
    {
      title: "Score",
      dataIndex: "percentage",
      width: 72,
      align: "center",
      render: (value) => (
        <strong className="assessment-score">{Number(value || 0)}%</strong>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 92,
      align: "center",
      render: getStatusTag,
    },
    {
      title: "Submitted",
      dataIndex: "submitted_at",
      width: 152,
      render: formatDateTime,
    },
  ];

  return (

    <div className="exam-management-page exam-management-results-page">

      <section className="exam-management-hero exam-management-results-hero">

        <div className="exam-management-hero-pattern" />

        <div className="exam-management-hero-content">

          <div className="exam-management-hero-copy">

            <div className="exam-management-breadcrumb">

              <span className="exam-management-breadcrumb-home">

                <HomeOutlined />

                <span>SM-ART</span>

              </span>

              <RightOutlined />

              <span>Exam Management</span>

              <RightOutlined />

              <strong>Assessment Results</strong>

            </div>

            <Title level={3}>Assessment Results</Title>

            <Text>

              View users and all assessment attempts across departments and levels.

            </Text>

          </div>

        </div>

      </section>

      <div className="exam-management-content-panel exam-management-results-panel">

        <div className="assessment-results-filters">

          <div className="assessment-filter-field assessment-filter-search">

            <Input

              allowClear

              value={searchInput}

              prefix={<SearchOutlined />}

              placeholder="Search username / full name / user ID"

              onChange={(event) => setSearchInput(event.target.value)}

              onPressEnter={handleSearch}

            />

          </div>
{/*
          <div className="assessment-filter-field">

            <Select

              allowClear

              showSearch

              loading={departmentLoading}

              value={departmentId || undefined}

              placeholder="All Departments"

              options={departmentOptions}

              optionFilterProp="searchText"

              optionLabelProp="selectedLabel"

              popupClassName="manage-training-department-dropdown"

              filterOption={(input, option) =>

                String(option?.searchText || "")

                  .toLowerCase()

                  .includes(String(input || "").toLowerCase())

              }

              onChange={(value) => {

                setDepartmentId(value || "");

                setExamId("");

                setUnitCode("");

                setPage(1);

              }}

            />

          </div> */}

          <div className="assessment-filter-field">

            <Select

              allowClear

              showSearch

              value={examId || undefined}

              placeholder="All Exams"

              options={examOptions}

              optionFilterProp="label"

              onChange={(value) => {

                setExamId(value || "");

                setPage(1);

              }}

            />

          </div>

          <div className="assessment-filter-field">

            <Select

              allowClear

              value={level || undefined}

              placeholder="All Levels"

              options={[

                { value: "level_1", label: "Level 1" },

                { value: "level_2", label: "Level 2" },

                { value: "level_3", label: "Level 3" },

              ]}

              onChange={(value) => {

                setLevel(value || "");

                setPage(1);

              }}

            />

          </div>

          <div className="assessment-filter-field">

            <Select

              allowClear

              showSearch

              value={unitCode || undefined}

              placeholder="All Units"

              options={unitOptions}

              optionFilterProp="label"

              onChange={(value) => {

                setUnitCode(value || "");

                setPage(1);

              }}

            />

          </div>

          <div className="assessment-filter-field assessment-filter-attempt">

            <InputNumber

              min={1}

              precision={0}

              value={attemptNo || null}

              placeholder="Attempt"

              onChange={(value) => {

                setAttemptNo(value || "");

                setPage(1);

              }}

            />

          </div>

          <div className="assessment-filter-field">

            <Select

              allowClear

              value={status || undefined}

              placeholder="All Status"

              options={[

                { value: "submitted", label: "Submitted" },

                { value: "started", label: "Started" },

              ]}

              onChange={(value) => {

                setStatus(value || "");

                setPage(1);

              }}

            />

          </div>

          <div className="assessment-filter-field assessment-filter-date">

            <RangePicker

              value={dateRange}

              allowClear

              onChange={(value) => {

                setDateRange(value);

                setPage(1);

              }}

            />

          </div>

        </div>

        <div className="assessment-results-filter-actions">

          <Button

            type="primary"

            icon={<SearchOutlined />}

            onClick={handleSearch}

            className="exam-upload-primary-button"

          >

            Search

          </Button>

          <Button

            onClick={handleReset}

            className="assessment-results-reset-button"

          >

            Reset

          </Button>

          <Button

            icon={<ReloadOutlined />}

            loading={loading}

            onClick={loadResults}

            className="assessment-results-refresh-button"

          >

            Refresh

          </Button>

        </div>

        <div className="assessment-results-summary">

          <div>

            <span>Department</span>

            <strong>

              {departmentId

                ? departmentOptions.find(

                    (item) => String(item.value) === String(departmentId)

                  )?.selectedLabel ||

                  `Department ${departmentId} • Dept ID: ${departmentId}`

                : "All Departments"}

            </strong>

          </div>

          <div>

            <span>Total Users</span>

            <strong>{total}</strong>

          </div>

          <div>

            <span>Current Page</span>

            <strong>{page}</strong>

          </div>

          <div>

            <span>Page Size</span>

            <strong>{pageSize}</strong>

          </div>

        </div>

        <div className="manage-exam-table-wrap">

          {loading ? (

            <div className="manage-exam-loading">

              <Spin size="large" />

              <span>Loading assessment results...</span>

            </div>

          ) : (

            <>

              <Table

                rowKey={(record) => String(record.user_id)}

                columns={columns}

                dataSource={rows}

                pagination={false}

                size="middle"

                scroll={{ x: "max-content" }}

                locale={{ emptyText: "No assessment results found" }}

              />

              <div className="exam-upload-pagination assessment-results-pagination">

                <span>

                  Showing{" "}

                  {total ? (page - 1) * pageSize + 1 : 0}–

                  {Math.min(page * pageSize, total)} of {total} users

                </span>

                <Pagination

                  responsive

                  showLessItems

                  current={page}

                  pageSize={pageSize}

                  total={total}

                  showSizeChanger

                  pageSizeOptions={[10, 20, 50, 100]}

                  onChange={(nextPage, nextPageSize) => {

                    if (nextPageSize !== pageSize) {

                      setPageSize(nextPageSize);

                      setPage(1);

                      return;

                    }

                    setPage(nextPage);

                  }}

                />

              </div>

            </>

          )}

        </div>

      </div>

      <Modal

        rootClassName="exam-assessment-modal-root"

        title="User Assessment Attempts"

        open={viewModalOpen}

        width={920}

        footer={[

          <Button

            key="close"

            onClick={() => {

              setViewModalOpen(false);

              setSelectedUser(null);

            }}

          >

            Close

          </Button>,

        ]}

        onCancel={() => {

          setViewModalOpen(false);

          setSelectedUser(null);

        }}

      >

        {selectedUser && (

          <div className="assessment-result-dialog assessment-result-dialog-compact">

            <div className="assessment-result-dialog-header assessment-result-dialog-header-compact">

              <div>

                <span>Employee / User</span>

                <strong>{selectedUser.full_name || selectedUser.username || "-"}</strong>

                <small>

                  Username: {selectedUser.username || "-"} • User ID:{" "}

                  {selectedUser.user_id ?? "-"}

                </small>

              </div>

              <Tag color="blue">

                {Number(selectedUser.total_attempts || 0)} Attempt

                {Number(selectedUser.total_attempts || 0) === 1 ? "" : "s"}

              </Tag>

            </div>

            <div className="assessment-result-dialog-grid assessment-result-dialog-grid-compact">

              <div>

                <span>Department</span>

                <strong>

                  {getDepartmentName(selectedUser) ||

                    departmentNameById.get(String(selectedUser.department_id)) ||

                    `Department ${selectedUser.department_id || "-"}`}

                </strong>

                <small>Dept ID: {selectedUser.department_id ?? "-"}</small>

              </div>

              <div>

                <span>Unit</span>

                <strong>{selectedUser.unit_name || "-"}</strong>

                <small>{selectedUser.unit_code ?? "-"}</small>

              </div>

              <div>

                <span>Total Attempts</span>

                <strong>{Number(selectedUser.total_attempts || 0)}</strong>

              </div>

              <div>

                <span>Latest Activity</span>

                <strong>{formatDateTime(selectedUser.latest_activity_at)}</strong>

              </div>

            </div>

            <div className="assessment-attempts-table-compact">

              <Table

                rowKey={(record) => String(record.attempt_id)}

                columns={attemptColumns}

                dataSource={

                  Array.isArray(selectedUser.attempts)

                    ? selectedUser.attempts

                    : []

                }

                pagination={false}

                size="small"

                scroll={{ x: 780 }}

                locale={{ emptyText: "No attempts found" }}

              />

            </div>

          </div>

        )}

      </Modal>

    </div>

  );

}
